import { useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Input } from '../../../components/primitives/Input';
import { Select } from '../../../components/primitives/Select';
import { VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { detectDeviceTimezone, listTimezones } from '../../../i18n/timezones';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useUpdateOrganization } from '../hooks/useSettings';
import { SettingsSection } from './SettingsSection';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  pt: 'pt-BR',
};

export function OrganizationSection() {
  const { backendUser, currentOrganization, setCurrentOrganization, refreshUser } = useAuth();
  const { t, language } = useTranslation();
  const updateOrg = useUpdateOrganization();

  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    id?: string;
    role: string;
  }> =
    ((backendUser as unknown as Record<string, unknown>)
      ?.organizationMembers as typeof memberships) ??
    ((backendUser as unknown as Record<string, unknown>)?.organizations as typeof memberships) ??
    [];
  const orgMembership = memberships.find(
    (o) => (o.organization?.id ?? o.organizationId ?? o.id) === currentOrganization?.id,
  );
  const isOwner = orgMembership?.role === 'owner';

  const savedTimezone: string | null = currentOrganization?.timezone ?? null;
  const deviceTimezone = useMemo(() => detectDeviceTimezone(), []);

  const [orgName, setOrgName] = useState(currentOrganization?.name ?? '');
  const [timezone, setTimezone] = useState<string | null>(savedTimezone);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (currentOrganization?.name) {
      setOrgName(currentOrganization.name);
    }
  }, [currentOrganization?.name]);

  useEffect(() => {
    setTimezone(savedTimezone);
  }, [savedTimezone]);

  const timezoneOptions = useMemo(
    () =>
      listTimezones([savedTimezone, timezone, deviceTimezone]).map((zone) => ({
        value: zone,
        label: zone,
      })),
    [savedTimezone, timezone, deviceTimezone],
  );

  const nameChanged = orgName.trim() !== (currentOrganization?.name ?? '');
  const timezoneChanged = timezone !== null && timezone !== savedTimezone;
  const hasChanges = nameChanged || timezoneChanged;
  const showDeviceSuggestion = isOwner && !!deviceTimezone && deviceTimezone !== timezone;

  const handleSave = () => {
    if (!currentOrganization?.id || !orgName.trim()) return;

    updateOrg.mutate(
      {
        orgId: currentOrganization.id,
        name: orgName.trim(),
        ...(timezoneChanged && timezone !== null && { timezone }),
      },
      {
        onSuccess: () => {
          setCurrentOrganization({
            ...currentOrganization,
            name: orgName.trim(),
            ...(timezoneChanged && { timezone }),
          });
          refreshUser();
          setSaveStatus('success');
          setTimeout(() => setSaveStatus('idle'), 3000);
        },
        onError: () => {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus('idle'), 4000);
        },
      },
    );
  };

  const createdDate = currentOrganization?.createdAt
    ? new Date(currentOrganization.createdAt).toLocaleDateString(LOCALE_MAP[language] ?? 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  return (
    <SettingsSection title={t('settings.organization.title')}>
      <Input
        label={t('settings.organization.nameLabel')}
        value={orgName}
        onChangeText={setOrgName}
        editable={isOwner}
        placeholder={t('settings.organization.namePlaceholder')}
      />

      <VStack spacing="sm" style={styles.timezoneField}>
        <Text variant="bodySmall" color={colors.text.primary} style={styles.label}>
          {t('settings.organization.timezoneLabel')}
        </Text>
        {isOwner ? (
          <Select
            variant="dropdown"
            options={timezoneOptions}
            value={timezone ?? ''}
            onChange={setTimezone}
            placeholder={t('settings.organization.timezoneNotSet')}
            searchable
            searchPlaceholder={t('common.selectSearchPlaceholder')}
            emptySearchLabel={t('common.selectNoMatches')}
            accessibilityLabel={t('settings.organization.timezoneLabel')}
            testID="org-timezone-select"
          />
        ) : (
          <Text variant="body" color={colors.text.secondary}>
            {timezone ?? t('settings.organization.timezoneNotSet')}
          </Text>
        )}
        <Text variant="caption" color={colors.text.tertiary}>
          {t(
            savedTimezone === null
              ? 'settings.organization.timezoneUnsetHint'
              : 'settings.organization.timezoneHint',
          )}
        </Text>
        {showDeviceSuggestion && (
          <Box style={styles.buttonRow}>
            <Button
              variant="ghost"
              size="sm"
              onPress={() => setTimezone(deviceTimezone)}
              accessibilityLabel={`${t('settings.organization.useDeviceTimezone')}: ${deviceTimezone}`}
              testID="org-timezone-use-device"
            >
              {`${t('settings.organization.useDeviceTimezone')}: ${deviceTimezone}`}
            </Button>
          </Box>
        )}
      </VStack>

      <VStack spacing="sm">
        <Text variant="bodySmall" color={colors.text.primary} style={styles.label}>
          {t('settings.organization.yourRole')}
        </Text>
        <Badge color={isOwner ? 'purple' : 'cyan'}>
          {orgMembership?.role ?? t('common.fallbacks.member')}
        </Badge>
      </VStack>

      <VStack spacing="sm">
        <Text variant="bodySmall" color={colors.text.primary} style={styles.label}>
          {t('settings.organization.created')}
        </Text>
        <Text variant="body" color={colors.text.secondary}>
          {createdDate}
        </Text>
      </VStack>

      {!isOwner && (
        <Box backgroundColor={colors.status.warning.bg} borderRadius="md" padding="md">
          <Text variant="bodySmall" color={colors.status.warning.main}>
            {t('settings.organization.ownerOnly')}
          </Text>
        </Box>
      )}

      {saveStatus === 'success' && (
        <Text variant="bodySmall" color={colors.status.success.main}>
          {t('settings.organization.successMessage')}
        </Text>
      )}
      {saveStatus === 'error' && (
        <Text variant="bodySmall" color={colors.status.error.main}>
          {t('settings.organization.errorMessage')}
        </Text>
      )}

      {isOwner && (
        <Box style={styles.buttonRow}>
          <Button
            variant="primary"
            onPress={handleSave}
            loading={updateOrg.isPending}
            disabled={updateOrg.isPending || !hasChanges || !orgName.trim()}
            accessibilityLabel={
              updateOrg.isPending ? t('common.states.saving') : t('common.actions.saveChanges')
            }
          >
            {updateOrg.isPending ? t('common.states.saving') : t('common.actions.saveChanges')}
          </Button>
        </Box>
      )}
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  label: {
    fontWeight: '500',
  },
  buttonRow: {
    alignSelf: 'flex-start',
  },
  timezoneField: {
    position: 'relative',
    zIndex: tokens.zIndex.dropdown,
  },
});
